import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authService, User } from "../services/firebase";

interface LoginResponse {
  token: string;
  user: User;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // האזנה לשינויים במצב ההתחברות
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser) => {
      
      if (firebaseUser) {
        // משתמש מחובר
        try {
          // בדיקת תקינות ה-token
          const token = await firebaseUser.getIdToken(true); // force refresh
          localStorage.setItem("token", token);

          setIsAuthenticated(true);
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email!,
            displayName: firebaseUser.displayName || undefined,
          });
        } catch (error) {
                    setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem("token");
        }
      } else {
        // משתמש לא מחובר
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem("token");
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = (await authService.login(
        email,
        password
      )) as LoginResponse;
      localStorage.setItem("token", response.token);
      setIsAuthenticated(true);
      setUser(response.user);
      navigate("/admin/dashboard");
    } catch (error) {
            throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem("token");
      navigate("/admin/login");
    } catch (error) {
          } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, login, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
